<template>
  <CustomerNavbar />

  <div id="map" class="map"></div>
  <div class="custom-attribution">
    Leaflet | © <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener noreferrer">OpenStreetMap</a>
    {{ t('map.contributors') }}
  </div>

  <div class="map-zoom-controls">
    <button @click="zoomIn">
      <img :src="plus" alt="Zoom In"/>
    </button>
    <button @click="zoomOut">
      <img :src="minus" alt="Zoom Out"/>
    </button>
    <button @click="goToUser">
      <img :src="locationCorsshair" alt="Go to User Location"/>
    </button>
  </div>

  <button @click="drawerIsOpen = !drawerIsOpen" class="locations-drawer-toggle"
    :class="{ 'locations-toggle-open': drawerIsOpen }">
    <img v-if="!drawerIsOpen" :src="chevronRight" alt="Open Drawer" class="locations-toggle-icon"/>
    <img v-if="drawerIsOpen" :src="xIcon" alt="Close Drawer" class="locations-toggle-icon"/>
  </button>

  <div class="locations-drawer" :class="[{ 'locations-drawer-open': drawerIsOpen }, 
    selectedRestaurant && !isDesktop ? 'mobile-details' : 'normal-drawer-content']">

    <div class="locations-drawer-header">
      <img v-if="selectedRestaurant" :src="chevronLeft" alt="Back" class="header-back-icon" @click="selectedRestaurant = null"/>
      <div class="locations-drawer-header-content">
        <div v-if="!selectedRestaurant" class="city-filter-container">
          <CustomSelect :options="cityOptions" :current="filterCity" @select="filterCity = $event" />
          <p>{{ `${filteredLocations.length} ${t('map.locations_found')}` }}</p>
        </div>
        <div v-else>
          <h2>{{ selectedRestaurant.companyName || selectedRestaurant.displayName }}</h2>
          <p>{{ `${t('map.restaurant_details')}` }}</p>
        </div>
      </div>
    </div>

    <RestaurantList v-if="!selectedRestaurant" :restaurants="filteredLocations" @select="selectedRestaurant = $event"/>

    <RestaurantDetails v-else :restaurant="selectedRestaurant" />

  </div>
</template>

<script setup>
import { ref, shallowRef, onMounted, watch, computed, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import 'leaflet/dist/leaflet.css';
import L, { Icon } from 'leaflet';

import redPin from '@/assets/icons/red-pin.png';
import chevronLeft from '@/assets/icons/chevron-left-solid.svg';
import chevronRight from '@/assets/icons/chevron-right-solid.svg';
import xIcon from '@/assets/icons/x-solid.svg';
import locationCorsshair from '@/assets/icons/location-crosshairs-solid.svg';
import minus from '@/assets/icons/minus-solid.svg';
import plus from '@/assets/icons/plus-solid.svg';

import { CustomerNavbar, CustomSelect } from "@/components";
import RestaurantList from './map/RestaurantsList.vue';
import RestaurantDetails from './map/RestaurantDetails.vue';

// Bulma’s $desktop breakpoint is 1024 px, used for quality of life improvements for mobile
const isDesktop = ref(window.innerWidth >= 1024);

const drawerIsOpen = ref(isDesktop.value);
const userLocation = ref(null);
const selectedRestaurant = ref(null);
const filterCity = ref('all');
const map = shallowRef(null);
const markers = shallowRef(null);
const { t } = useI18n();

// calculate distance in km between two coordinates using Haversine formula
const calculateDistance = (lat1, lon1, lat2, lon2) => {
  const R = 6371; // radius of the earth in km
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = 
    Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
    Math.sin(dLon/2) * Math.sin(dLon/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  return R * c;
};

const sortedLocations = computed(() => {
  if (!userLocation.value) return [];
  return [...props.locations].map(location => {
    const distance = calculateDistance(
      userLocation.value.lat, userLocation.value.lng, location.coordinates.latitude, location.coordinates.longitude
    );
    return { ...location, distance };
  }).sort((a, b) => a.distance - b.distance);
});

const cityOptions = computed(() => {
  const cities = props.locations.map(r => r.city).filter(c => c && c != " ");
  return Array.from(new Set(cities)).sort();
});

const filteredLocations = computed(() => {
  if (filterCity.value === 'all') return sortedLocations.value;
  return sortedLocations.value.filter(r => r.city === filterCity.value);
});

const goToUser = () => {
  if (userLocation.value && map.value) {
    map.value.setView([userLocation.value.lat, userLocation.value.lng]);
  }
};

const zoomIn = () => map.value && map.value.zoomIn();
const zoomOut = () => map.value && map.value.zoomOut();

// point Leaflet at the right files since vue bundler doesn't preserve Leaflet's default relative path
delete Icon.Default.prototype._getIconUrl
Icon.Default.mergeOptions({
  iconRetinaUrl: new URL('leaflet/dist/images/marker-icon-2x.png', import.meta.url).href,
  iconUrl:       new URL('leaflet/dist/images/marker-icon.png',   import.meta.url).href,
  shadowUrl:     new URL('leaflet/dist/images/marker-shadow.png',  import.meta.url).href,
});
const redIcon = new L.Icon({
  iconUrl: redPin,
  shadowUrl: new URL('leaflet/dist/images/marker-shadow.png', import.meta.url).href,
  iconSize:     [25, 41],
  iconAnchor:   [12, 41],
  popupAnchor:  [1, -34],
  shadowSize:   [41, 41]
});

const props = defineProps({
  locations: {
    type: Array,
    default: () => [],
  },
});

onMounted(() => {
  map.value = L.map('map', {
    zoomControl: false,
    attributionControl: false
  });
  markers.value = L.layerGroup();
  markers.value.addTo(map.value);

  L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
    maxZoom: 19
  }).addTo(map.value);

  navigator.geolocation.getCurrentPosition((position) => {
    const lat = position.coords.latitude;
    const lng = position.coords.longitude;
    userLocation.value = { lat, lng };
    L.marker([lat, lng]).addTo(markers.value).bindPopup(t('map.you_are_here')).openPopup();
    map.value.setView([lat, lng], 13);
  }, () => { // Fallback to toronto if request permission fails
    userLocation.value = { lat: 43.6532, lng: -79.3832 };
    map.value.setView([43.6532, -79.3832], 13);
  });

  props.locations.forEach((r) => {
    const lat = r.coordinates.latitude;
    const lng = r.coordinates.longitude;
    const displayName = r.companyName || r.displayName || 'Unnamed';
    const address = r.streetAddress || `${lat}, ${lng}`;
    var customPopup = `<div class="custom-popup">
      <div class="popup-name">${displayName}</div>
      <div class="popup-address">${address}</div>
    </div>`;
    const marker = L.marker([lat, lng], { icon: redIcon }).addTo(markers.value).bindPopup(customPopup);
    marker.on('click', () => {
      const distance = calculateDistance(
        userLocation.value.lat, userLocation.value.lng, r.coordinates.latitude, r.coordinates.longitude
      );
      selectedRestaurant.value = { ...r, distance };
      if (isDesktop.value) {
        drawerIsOpen.value = true;
      }
    });
  });
});

// invalidate map size when drawer opens/closes so Leaflet recalculates layout cleanly
watch(drawerIsOpen, () => {
  if (map.value) nextTick(() => map.value.invalidateSize());
});

// set map view to selected restaurant
watch(selectedRestaurant, (newVal) => {
  if (newVal && map.value) {
    const lat = newVal.coordinates.latitude;
    const lng = newVal.coordinates.longitude;
    markers.value.eachLayer(layer => {
      if (layer.getLatLng().lat === lat && layer.getLatLng().lng === lng) {
        layer.openPopup();
      } else {
        layer.closePopup();
      }
    });
    map.value.setView([lat, lng], 15);
  }
});
</script>


<style lang="scss">
@import "~bulma/sass/utilities/_all.sass";

.map {
  height: 100vh;
  width: 100vw;
  position: fixed;
}

.custom-attribution {
  position: fixed;
  bottom: 12px;
  right: 12px;
  padding: 4px 8px;

  background: rgba(255, 255, 255, 0.5); // semi-transparent white
  color: #000;
  font-size: 0.75rem;
  border-radius: 4px;

  z-index: 10; // sit above elements of the map
}

.custom-popup {
  padding: 0;
  margin: 0;
}
.popup-name {
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 2px;
}
.popup-address {
  font-size: 0.85rem;
}

.locations-toggle-icon {
  width: 20px;
  height: 20px;
}

.locations-drawer-toggle {
  position: fixed;
  top: 100px;
  left: 15px;
  width: 40px;
  height: 40px;
  z-index: 12;
  background-color: white;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  transition: transform 0.3s ease;

  @include until($desktop) {
    top: 85px;
  }
}

.locations-drawer {
  position: fixed;
  top: 80px;
  bottom: 10px;
  left: -340px;
  width: 330px;
  height: calc(100vh - 80px - 10px);
  z-index: 11;
  background-color: white;
  display: flex;
  flex-direction: column;
  transition: transform 0.3s ease;

  @include until($desktop) {
    top: 65px;
    width: 300px;
    &.normal-drawer-content {
      height: calc(80vh - 65px - 10px);
    }
    &.mobile-details {
      height: 0;
    }
  }
}

.locations-drawer-open {
  transform: translateX(350px);
}
.locations-toggle-open {
  transform: translateX(275px);
  @include until($desktop) {
    transform: translateX(245px);
  }
}

.locations-drawer-header {
  padding: 20px;
  background-color: #f8f8f8;
  display: flex;
  justify-content: left;
  align-items: center;
  .header-back-icon {
    width: 20px;
    height: 20px;
    color: #333;
    margin-right: 15px;
  }
  h2 {
    font-size: 1.25rem;
  }
  p {
    font-size: 0.875rem;
  }
  .locations-drawer-header-title {
    display: flex;
    align-items: center;
    justify-content: space-between;
    img {
      margin-left: 20;
      height: 20px;
      width: 20px;
    }
  }
}

.locations-toggle-icon, .restaurant-drawer-item, .header-back-icon {
  cursor: pointer;
}

.map-zoom-controls{
  position: fixed;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  top: 95px;
  right: 30px;
  height: 100px;
  button {
    width: 30px;
    height: 30px;
    background-color: white;
    &:hover {
      background-color: #c9e3f5;
    }
    border-radius: 30%;
    display: flex;
    justify-content: center;
    align-items: center;
    img {
      height: 20px;
      width: 20px;
    }
  }
}

.locations-drawer-header-content {
  flex: 1;
}
.city-filter-container {
  width: 100%;
}
</style>